import {
  decimalToPip,
  dividePips,
  multiplyPips,
} from '@katanaperps/katana-perps-sdk';

import { trimTo8Decimals } from '#/lib/numbers';

/**
 * Profit Share vault ownership is tracked in shares - the USDC value of a
 * holding is `shares * sharePrice`. Both inputs and outputs are in pips.
 */
export const sharesToQuantityPips = (shares: bigint, sharePrice: bigint) =>
  multiplyPips(shares, sharePrice);

/**
 * Shares (pips) representing `quantity` USDC (pips) at `sharePrice` (pips),
 * rounded up so the burned shares cover the requested value.
 * Returns 0 for a zero share price (vault without shares yet).
 */
export const quantityToSharesPips = (quantity: bigint, sharePrice: bigint) => {
  if (sharePrice <= 0n) {
    return 0n;
  }
  const shares = dividePips(quantity, sharePrice);
  return multiplyPips(shares, sharePrice) < quantity ? shares + 1n : shares;
};

/**
 * Shares (pips) for a user entered USDC amount. When the amount reaches the
 * full available quantity, the exact available shares are used instead of
 * the computed value - avoids rounding past the wallet's share balance.
 */
export const quantityDecimalToShares = ({
  quantity,
  sharePrice,
  sharesAvailable,
}: {
  quantity: string | number;
  sharePrice: bigint;
  sharesAvailable: bigint;
}) => {
  const quantityPips = decimalToPip(trimTo8Decimals(quantity));
  const shares = quantityToSharesPips(quantityPips, sharePrice);
  return shares > sharesAvailable ? sharesAvailable : shares;
};

/**
 * Carry fee (pips) the manager takes from a withdrawal of `shares`: the fee
 * multiplier applied to the realised profit of the burned shares, where the
 * profit is their value at `sharePrice` above the proportional part of the
 * wallet's cost basis (`costBasis * shares / totalShares`). No fee without a
 * profit; the manager pays no carry fee on their own holdings (caller's check).
 *
 * @example 100 shares bought for $100, share price $1.60, 10% carry fee:
 *   burning 30 shares is worth $48, its cost basis part is $30, so the fee is
 *   10% of the $18 profit = $1.80
 */
export const getShareWithdrawalCarryFeePips = ({
  shares,
  totalShares,
  costBasis,
  sharePrice,
  carryFeeMultiplier,
}: {
  /** Shares burned by the withdrawal */
  shares: bigint;
  /** All shares held by the wallet, the cost basis refers to them */
  totalShares: bigint;
  costBasis: bigint;
  sharePrice: bigint;
  /** Fee multiplier in pips, 0.1 = 10% */
  carryFeeMultiplier: bigint;
}) => {
  if (shares <= 0n || totalShares <= 0n) {
    return 0n;
  }
  const grossQuantity = sharesToQuantityPips(shares, sharePrice);
  const costBasisPortion = (costBasis * shares) / totalShares;
  const profit = grossQuantity - costBasisPortion;
  if (profit <= 0n) {
    return 0n;
  }
  return multiplyPips(profit, carryFeeMultiplier);
};

/**
 * Slippage tolerance of a share withdrawal: the share price can move between
 * the request and its processing (queued withdrawals wait for liquidity), so
 * the minimum accepted USDC quantity sits this far below the quoted value. A
 * withdrawal below the minimum fails on-chain (only the failure fee is charged).
 */
export const VAULT_SHARE_WITHDRAWAL_SLIPPAGE_TOLERANCE = 0.05;

/**
 * Minimum USDC quantity accepted for a share withdrawal. `quantity` is the
 * value of the burned shares net of the carry fee - the fee is deducted from
 * the withdrawal, so the floor has to sit below what remains after it.
 */
export const getShareWithdrawalMinimumQuantity = (quantity: string | number) =>
  trimTo8Decimals(
    Number(quantity) * (1 - VAULT_SHARE_WITHDRAWAL_SLIPPAGE_TOLERANCE)
  );
